import { useState, useEffect, useRef, useCallback } from 'react';
import { Activity, AlertCircle, Filter, X, ChevronDown } from 'lucide-react';
import { Loader } from '../components/UI/LoadingSpinner';
import EntityGroupCard from '../components/UI/EntityGroupCard';
import EntityEventDetail from '../components/UI/EntityEventDetail';
import { users as usersApi, events as eventsApi } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import eventTypesData from '../../../shared/eventTypes.json';
import SavedFiltersDropdown from '../components/SavedFilters/SavedFiltersDropdown';
import { DatePicker, Select, Autocomplete } from '@anyemp/ui-kit';
import { PAGE_KEYS } from '../constants/savedFilterPageKeys';

const SAVED_FILTER_KEYS = ['event_types', 'eventable_type', 'user_id', 'date_from', 'date_to'];

const { EVENT_LABELS, EVENT_TYPES } = eventTypesData;

const EVENT_TYPE_OPTIONS = EVENT_TYPES.map(type => ({
  value: type,
  label: EVENT_LABELS[type] || type.replace(/_/g, ' '),
}));

const ENTITY_TYPE_OPTIONS = [
  { value: 'Talent', label: 'Talent' },
  { value: 'JobApplication', label: 'Job Application' },
  { value: 'TalentPost', label: 'Talent Post' },
  { value: 'TalentTemplate', label: 'Talent Template' },
  { value: 'PromptTemplate', label: 'Prompt Template' },
];

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'my', label: 'My' },
];

// ── Main Page ─────────────────────────────────────────────────────────
const ActivityFeed = () => {
  const { hasPermission, user } = useAuth();
  const canCancel = hasPermission('talent-service', 'events', 'cancel');
  const currentUserId = user?.id;

  const [activeTab, setActiveTab] = useState('all');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    event_types: [],
    eventable_type: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });

  const pageRef = useRef(1);
  const isLoadingRef = useRef(false);
  // Ref to the left scrollable panel (used in split mode)
  const leftPanelRef = useRef(null);
  // Ref to the last group card (used in page-scroll mode)
  const lastCardObserverRef = useRef(null);

  const isDetailOpen = selectedGroup !== null;

  // Load users once on mount
  useEffect(() => {
    usersApi.getAllUsers()
      .then(res => {
        const list = res?.success
          ? (Array.isArray(res.data) ? res.data : (res.data?.users || res.data?.data || []))
          : (Array.isArray(res) ? res : []);
        const map = {};
        list.forEach(u => {
          const name = (u.first_name || u.last_name)
            ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
            : (u.name || u.email || String(u.id));
          map[u.id] = { name, avatar: u.avatar || u.photo || null };
        });
        setUsersMap(map);
      })
      .catch(console.error);
  }, []);

  const fetchGroups = useCallback(async (page, currentFilters, tab) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    if (page === 1) setLoading(true);

    try {
      const params = { page, limit: 20 };

      if (currentFilters.event_types.length > 0) {
        params['event_types[]'] = currentFilters.event_types;
      }
      if (currentFilters.eventable_type) {
        params['eventable_types[]'] = currentFilters.eventable_type;
      }

      const effectiveUserId = tab === 'my' ? currentUserId : currentFilters.user_id;
      if (effectiveUserId) params['user_ids[]'] = effectiveUserId;

      if (currentFilters.date_from) params.date_from = currentFilters.date_from;
      if (currentFilters.date_to) params.date_to = currentFilters.date_to;

      const res = await eventsApi.getGroupedEvents(params);
      const newGroups = res.data || [];

      setGroups(prev => page === 1 ? newGroups : [...prev, ...newGroups]);
      setTotal(res.pagination.total);
      setHasMore(page < res.pagination.totalPages);
      pageRef.current = page;
      setError(null);
    } catch {
      setError('Failed to load activity feed');
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [currentUserId]);

  // Reload on filter or tab change
  useEffect(() => {
    pageRef.current = 1;
    setGroups([]);
    setSelectedGroup(null);
    setHasMore(true);
    fetchGroups(1, filters, activeTab);
  }, [filters, fetchGroups, activeTab]);

  // ── Infinite scroll: page-scroll mode (no detail panel) ──────────
  // IntersectionObserver on last group card, root = viewport
  const lastCardRef = useCallback(node => {
    if (lastCardObserverRef.current) lastCardObserverRef.current.disconnect();
    if (isDetailOpen) return; // handled by panel scroll event below
    lastCardObserverRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingRef.current) {
        fetchGroups(pageRef.current + 1, filters, activeTab);
      }
    }, { threshold: 0.1 });
    if (node) lastCardObserverRef.current.observe(node);
  }, [isDetailOpen, hasMore, filters, fetchGroups, activeTab]);

  // ── Infinite scroll: split-pane mode (detail panel open) ─────────
  // Scroll event listener on the left panel ref
  useEffect(() => {
    if (!isDetailOpen) return;
    const panel = leftPanelRef.current;
    if (!panel) return;

    const onScroll = () => {
      if (!hasMore || isLoadingRef.current) return;
      if (panel.scrollHeight - panel.scrollTop - panel.clientHeight < 300) {
        fetchGroups(pageRef.current + 1, filters, activeTab);
      }
    };

    panel.addEventListener('scroll', onScroll, { passive: true });
    return () => panel.removeEventListener('scroll', onScroll);
  }, [isDetailOpen, hasMore, filters, fetchGroups, activeTab]);

  const handleSelectGroup = (group) => {
    setSelectedGroup(prev =>
      prev?.entityType === group.entityType && prev?.entityId === group.entityId
        ? null
        : group
    );
  };

  const hasActiveFilters =
    filters.event_types.length > 0 ||
    filters.eventable_type !== '' ||
    (activeTab !== 'my' && filters.user_id !== '') ||
    filters.date_from !== '' ||
    filters.date_to !== '';

  const activeFilterCount =
    (filters.event_types.length > 0 ? 1 : 0) +
    [
      filters.eventable_type,
      activeTab !== 'my' ? filters.user_id : '',
      filters.date_from,
      filters.date_to,
    ].filter(v => v !== '').length;

  const clearFilters = () => setFilters({
    event_types: [], eventable_type: '', user_id: '', date_from: '', date_to: '',
  });

  // ── Left panel content (shared between both layout modes) ─────────
  const renderControls = () => (
    <>
      {/* Header */}
      <div className="flex min-w-0 flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          {!isDetailOpen && (
            <h1 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
              Activity Feed
            </h1>
          )}
          {total > 0 && (
            <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
              ({total.toLocaleString()})
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <SavedFiltersDropdown
            pageKey={PAGE_KEYS.ACTIVITY_FEED}
            currentFilters={filters}
            applyKeys={SAVED_FILTER_KEYS}
            onApply={(saved) => setFilters(prev => ({ ...prev, ...saved }))}
          />
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`flex min-h-11 items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-colors ${
              hasActiveFilters
                ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary'
            }`}
          >
            <Filter className="h-3.5 w-3.5" />
            {!isDetailOpen && <span>Filters</span>}
            {hasActiveFilters && (
              <span className="ml-1 bg-primary-600 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-light-border-primary dark:border-dark-border-primary mb-4">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="mb-4 p-4 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-lg space-y-3">
          <div className={`grid min-w-0 gap-3 ${isDetailOpen ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
            <div className="min-w-0">
              <DatePicker
                label="Date From"
                size="small"
                fullWidth
                value={filters.date_from ? new Date(filters.date_from) : null}
                onChange={(d) => setFilters(f => ({ ...f, date_from: d ? d.toISOString().slice(0, 10) : '' }))}
              />
            </div>
            <div className="min-w-0">
              <DatePicker
                label="Date To"
                size="small"
                fullWidth
                value={filters.date_to ? new Date(filters.date_to) : null}
                onChange={(d) => setFilters(f => ({ ...f, date_to: d ? d.toISOString().slice(0, 10) : '' }))}
              />
            </div>
            <Select
              label="Entity Type"
              size="small"
              fullWidth
              placeholder="All entity types"
              value={filters.eventable_type || ''}
              onChange={(v) => setFilters(f => ({ ...f, eventable_type: v || '' }))}
              options={ENTITY_TYPE_OPTIONS}
            />
            {activeTab === 'all' && (
              <Select
                label="Manager / User"
                size="small"
                fullWidth
                placeholder="All users"
                value={filters.user_id || ''}
                onChange={(v) => setFilters(f => ({ ...f, user_id: v || '' }))}
                options={Object.entries(usersMap).map(([id, u]) => ({ value: String(id), label: u.name }))}
              />
            )}
          </div>
          <div>
            <Autocomplete multiple clearable label="Event Type" options={EVENT_TYPE_OPTIONS} value={filters.event_types}
              onChange={val => setFilters(f => ({ ...f, event_types: val || [] }))}
              placeholder="All event types..."
            />
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 transition-colors">
              <X className="h-3 w-3" /> Clear all filters
            </button>
          )}
        </div>
      )}
    </>
  );

  const renderGroupList = () => (
    loading && groups.length === 0 ? (
      <Loader variant="component" size="md" fullHeight={false} />
    ) : error ? (
      <div className="text-center py-10">
        <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-600 dark:text-red-400 mb-3">{error}</p>
        <button onClick={() => fetchGroups(1, filters, activeTab)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 transition-colors">
          Try Again
        </button>
      </div>
    ) : groups.length === 0 ? (
      <div className="text-center py-10 text-light-text-tertiary dark:text-dark-text-tertiary">
        <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p>No activity found</p>
      </div>
    ) : (
      <div className="space-y-2">
        {groups.map((group, idx) => {
          const key = `${group.entityType}_${group.entityId}`;
          const isLast = idx === groups.length - 1;
          const isSelected = selectedGroup?.entityType === group.entityType && selectedGroup?.entityId === group.entityId;
          return (
            <div key={key} ref={!isDetailOpen && isLast ? lastCardRef : null}>
              <EntityGroupCard group={group} usersMap={usersMap} isSelected={isSelected} onSelect={handleSelectGroup} />
            </div>
          );
        })}

        {loading && (
          <div className="flex justify-center py-3">
            <Loader variant="component" size="sm" fullHeight={false} />
          </div>
        )}

        {!hasMore && groups.length > 0 && (
          <p className="text-center text-xs text-light-text-tertiary dark:text-dark-text-tertiary py-3">
            All {total.toLocaleString()} entities loaded
          </p>
        )}
      </div>
    )
  );

  // ── Render ────────────────────────────────────────────────────────

  if (!isDetailOpen) {
    // Single-column, normal page scroll
    return (
      <div className="mx-auto w-full min-w-0 max-w-3xl">
        {renderControls()}
        {renderGroupList()}
      </div>
    );
  }

  // Phones use a full-width detail view. The persistent split pane starts at
  // lg, where both master and detail remain wide enough to be useful.
  return (
    <div className="h-[calc(100dvh-7rem)] min-w-0 overflow-hidden lg:flex lg:h-[calc(100dvh-3rem)]">

      {/* Left panel — independent scroll */}
      <div
        ref={leftPanelRef}
        className="hidden w-80 shrink-0 overflow-y-auto border-r border-light-border-primary p-4 dark:border-dark-border-primary lg:block"
      >
        {renderControls()}
        {renderGroupList()}
      </div>

      {/* Right panel — EntityEventDetail manages its own scroll */}
      <div className="h-full min-w-0 overflow-hidden lg:flex-1 lg:p-4">
        <EntityEventDetail
          key={`${selectedGroup.entityType}_${selectedGroup.entityId}`}
          group={selectedGroup}
          usersMap={usersMap}
          canCancel={canCancel}
          onClose={() => setSelectedGroup(null)}
        />
      </div>

    </div>
  );
};

export default ActivityFeed;
